/* ═══════════════════════════════════════════════════════════════
   ALTERLY — RGB Split / Chromatic Aberration Node
   Genuine optical channel displacement across custom angles.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const RGB_SPLIT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_amount;
uniform float u_angleRad;

void main() {
  vec2 dir = vec2(cos(u_angleRad), sin(u_angleRad));
  vec2 offset = (dir * u_amount) / u_resolution;

  vec4 rSample = texture(u_image, v_uv + offset);
  vec4 gSample = texture(u_image, v_uv);
  vec4 bSample = texture(u_image, v_uv - offset);

  fragColor = vec4(rSample.r, gSample.g, bSample.b, max(gSample.a, max(rSample.a, bSample.a)));
}
`;

export const rgbSplitNode: NodeDefinition = {
  type: 'filter.rgb_split',
  category: 'Filter',
  label: 'RGB Split',
  description: 'Simulates chromatic aberration and prismatic lens channel dispersion',
  keywords: ['rgb', 'split', 'chromatic', 'aberration', 'glitch', 'prism', 'lens', 'fringe'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Split', type: 'image' },
  ],
  params: [
    {
      id: 'amount',
      name: 'Shift Amount',
      type: 'slider',
      defaultValue: 10,
      min: 0,
      max: 60,
      step: 0.5,
      unit: 'px',
    },
    {
      id: 'angle',
      name: 'Dispersion Angle',
      type: 'angle',
      defaultValue: 0,
      min: 0,
      max: 360,
      step: 1,
      unit: '°',
      uiHint: 'angle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: RGB_SPLIT_SHADER,
    getUniforms: (inputs, params, dimensions) => {
      const angleDeg = Number(params.angle) || 0;
      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_resolution: [dimensions.width, dimensions.height],
        u_amount: Number(params.amount) || 10,
        u_angleRad: (angleDeg * Math.PI) / 180,
      };
    },
  }),
};
